import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Filler,
} from "chart.js";

import { palette } from "../../../app/utils/palette.js";
import ErrorState from "../../../app/universalComponents/ErrorState";

ChartJS.register(
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Filler,
);

export default function TimelineWidget({
  eventStatus,
  chart,
  duration,
  metric,
  onDurationChange,
  onMetricChange,
  refreshError,
}) {
  const chartData = useMemo(() => {
    const labels = Array.isArray(chart?.labels) ? chart.labels : [];
    const dataset =
      chart?.dataset && typeof chart.dataset === "object"
        ? chart.dataset
        : null;

    if (!dataset) {
      return {
        labels,
        datasets: [],
      };
    }

    if (dataset.type === "revenue") {
      const revenueData = Array.isArray(dataset.data)
        ? dataset.data.map((value) => {
            const number = Number(value ?? 0);

            return Number.isFinite(number) ? number : 0;
          })
        : [];

      return {
        labels,
        datasets: [
          {
            type: "line",
            label: dataset.label || "Revenue",
            data: revenueData,
            borderColor: "#000000",
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
            borderWidth: 2,
          },
        ],
      };
    }

    if (dataset.type === "tickets_sold") {
      const ticketTypeData = Array.isArray(dataset.data)
        ? dataset.data
        : [];

      return {
        labels,
        datasets: ticketTypeData
          .filter(Boolean)
          .map((ticketType, index) => {
            const values = Array.isArray(ticketType?.values)
              ? ticketType.values.map((value) => {
                  const number = Number(value ?? 0);

                  return Number.isFinite(number) ? number : 0;
                })
              : [];

            const fallbackColor = "#6b7280";
            const barColor =
              palette.length > 0
                ? palette[index % palette.length]
                : fallbackColor;

            return {
              type: "bar",
              label: ticketType?.ticket_type || "Ticket",
              data: values,
              backgroundColor: barColor,
              borderColor: barColor,
              borderWidth: 0,
              borderRadius: 2,
            };
          }),
      };
    }

    return {
      labels,
      datasets: [],
    };
  }, [chart]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,

      interaction: {
        mode: "index",
        intersect: false,
      },

      animation: {
        duration: 700,
        easing: "easeOutQuart",
      },

      plugins: {
        legend: {
          display: metric !== "revenue",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 6,
            boxHeight: 6,
          },
        },

        tooltip: {
          enabled: true,
          callbacks: {
            label: (context) => {
              const rawValue = Number(context.raw ?? 0);
              const value = Number.isFinite(rawValue) ? rawValue : 0;

              if (metric === "revenue") {
                return `Revenue: €${value.toFixed(2)}`;
              }

              return `${context.dataset.label || "Tickets"}: ${value}`;
            },
          },
        },
      },

      scales: {
        x: {
          stacked: metric !== "revenue",

          grid: {
            display: false,
          },

          ticks: {
            color: "#6a7282",
          },

          border: {
            display: false,
          },
        },

        y: {
          beginAtZero: true,
          stacked: metric !== "revenue",

          grid: {
            color: "#f3f4f6",
          },

          ticks: {
            color: "#6a7282",
            callback: (value) =>
              metric === "revenue" ? `€${value}` : value,
          },

          border: {
            display: false,
          },
        },
      },
    }),
    [metric],
  );

  if (eventStatus === "draft") {
    return (
      <ErrorState>
        A revenue and sales timeline will appear here once this event is
        published.
      </ErrorState>
    );
  }

  const safeDuration =
    duration === "all" || duration === "last_7_days"
      ? duration
      : "last_7_days";

  const safeMetric =
    metric === "tickets_sold" || metric === "revenue"
      ? metric
      : "revenue";

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="shrink-0">
        <div className="flex justify-end gap-2">
          <select
            disabled={Boolean(refreshError)}
            value={safeDuration}
            onChange={(event) => {
              onDurationChange?.(event.target.value);
            }}
            className="rounded-sm border border-gray-200 px-2 py-1 text-sm text-gray-500 shadow-sm outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="last_7_days">Last 7 days</option>
            <option value="all">From start</option>
          </select>

          <select
            disabled={Boolean(refreshError)}
            value={safeMetric}
            onChange={(event) => {
              onMetricChange?.(event.target.value);
            }}
            className="rounded-sm border border-gray-200 px-2 py-1 text-sm text-gray-500 outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="revenue">Revenue</option>
            <option value="tickets_sold">Tickets sold</option>
          </select>
        </div>
      </div>

      <div className="relative mt-4 min-h-0 flex-1">
        <Chart
          key={`${safeMetric}-${safeDuration}`}
          type={safeMetric === "revenue" ? "line" : "bar"}
          data={chartData}
          options={chartOptions}
        />
      </div>
    </div>
  );
}